import _get from 'lodash/get'
import _reduce from 'lodash/reduce'
import { get as getConfig } from 'config'
import {
  get as apiGet, post as apiPost, put as apiPut, TYPE_BROOKLYN_JSON, TYPE_GRAPHQL,
} from 'api-client'
import { getSessionStorage, setSessionStorage } from 'services/local-preferences'
import { RESOLVER_TYPE_NOT_FOUND } from 'services/resolver/types'

const PATH_EXP = /(\/logout|go|plan|cart|share|gift|events|account|checkout|get-started|live-access|terms-privacy|dev\/kitchen-sink)/i
export const MAX_PROFILES = 3
export const PROMPT_PROFILE_SELECTOR_KEY = 'PROMPT_PROFILE_SELECTOR'

export function initializePrompt ({
  featureTracking,
  profileCount,
  resolver,
  user,
  app,
}) {
  const pathname = resolver.get('path')
  const resolverType = resolver.getIn(['data', 'type'])
  const preference = featureTracking.getIn(['data', 'dismissedProfileChooser'], false)
  const checkoutComplete = user.getIn(['data', 'checkout_complete'])

  let shouldPrompt = false

  if (!checkoutComplete) {
    return shouldPrompt
  }

  if (!PATH_EXP.test(pathname)
    && !app.get('isWebView')
    && resolverType !== RESOLVER_TYPE_NOT_FOUND
    && profileCount > 0) {
    const sessionValue = getSessionStorage(PROMPT_PROFILE_SELECTOR_KEY, true)
    shouldPrompt = profileCount > 1 ? sessionValue : (sessionValue && !preference)
  }
  // idk if we should be setting this here...
  setSessionStorage(PROMPT_PROFILE_SELECTOR_KEY, shouldPrompt)
  return shouldPrompt
}

export async function postUserProfile (data) {
  try {
    const { auth } = data
    const res = await apiPost('v1/create-user-profile', data, { auth }, TYPE_BROOKLYN_JSON)
    const body = _get(res, 'body')

    if (!body) {
      throw new Error('Error creating user profiles, no response body')
    }

    if (_get(body, 'errors')) {
      const message = _get(res, 'body.errors[0].detail', 'Error creating profile')
      throw new Error(message)
    }
    return { data: body }
  } catch (e) {
    return {
      message: e.message,
      error: true,
    }
  }
}

export async function removeProfileFromAccount (data) {
  try {
    const { value } = data
    const { auth, profile } = value
    const res = await apiPut('v1/remove-user-profile', profile, { auth }, TYPE_BROOKLYN_JSON)
    const body = _get(res, 'body')

    if (!body) {
      throw new Error('Error removing user profiles, no response body')
    }
    return { data: body }
  } catch (e) {
    return {
      message: e.message,
      error: true,
    }
  }
}

// put the account owner first
function orderProfiles (profiles) {
  return _reduce(profiles, (reduction, profile) => {
    if (_get(profile, 'uid') === _get(profile, 'account_owner_uid')) {
      reduction.unshift(profile)
    } else {
      reduction.push(profile)
    }

    return reduction
  }, [])
}

export async function getProfiles (auth) {
  try {
    const res = await apiGet('v1/user-account', null, { auth }, TYPE_BROOKLYN_JSON)
    return { data: orderProfiles(_get(res, 'body', [])) }
  } catch (e) {
    return { data: [] }
  }
}

const CURRENT_ACCOUNT_PROFILES_QUERY = `
  query CurrentAccountProfiles {
    currentAccountProfiles {
      uid
      uuid
      email
      username
      firstName
      lastName
      displayName
      status
      birthday
      creationSource
      emailRegisteredExternally
      accountOwnerUid
      accountOwnerUuid
      userAccountId
      billingAccountId
      billingAccountNumber
      profilePicture {
        hdtv_190x266
        small_28x28
      }
      userInfo {
        uid
        avatar {
          url
          small
          card
        }
      }
    }
  }
`

/**
 * Fetch account profiles via a single federated GraphQL query.
 * Replaces the separate REST getProfiles + GraphQL getProfilePictures calls.
 * @param {Object} auth The auth token object
 * @returns {Promise<{userProfiles: Object, communityProfile: Object}>}
 */
export async function getAccountProfilesGraphQL (auth) {
  try {
    const config = getConfig()
    const graphqlUrl = `${_get(config, 'servers.graphql', '')}graphql`

    const res = await apiPost(
      graphqlUrl,
      { query: CURRENT_ACCOUNT_PROFILES_QUERY },
      { auth },
      TYPE_GRAPHQL,
    )

    const profiles = _get(res, ['body', 'data', 'currentAccountProfiles'], [])

    // Transform to match existing Redux shapes (snake_case for userProfiles)
    const profileData = profiles.map((p) => ({
      uid: p.uid,
      uuid: p.uuid,
      email: p.email,
      username: p.username,
      first_name: p.firstName,
      last_name: p.lastName,
      displayName: p.displayName,
      status: p.status,
      birthday: p.birthday,
      creation_source: p.creationSource,
      email_registered_externally: p.emailRegisteredExternally,
      account_owner_uid: p.accountOwnerUid,
      account_owner_uuid: p.accountOwnerUuid,
      user_account_id: p.userAccountId,
      billing_account_id: p.billingAccountId,
      billing_account_number: p.billingAccountNumber,
      profilePicture: p.profilePicture,
    }))

    // Build avatar map keyed by uid
    const profilePictures = {}
    profiles.forEach((p) => {
      const avatar = _get(p, ['userInfo', 'avatar'])
      if (p.uid && avatar) {
        profilePictures[String(p.uid)] = avatar
      }
    })

    return {
      userProfiles: { data: orderProfiles(profileData) },
      communityProfile: { profilePictures: { data: profilePictures } },
    }
  } catch (e) {
    return {
      userProfiles: { data: [] },
      communityProfile: { profilePictures: { data: {} } },
    }
  }
}
